import { useSummaryV2 } from '@/hooks/useCompany';
import {
  useCompanyLimits,
  useGetMutateCompanyLimit,
} from '@/hooks/useCompanyLimits';
import type { IntersectionPayload } from '@/hooks/useIntersectionListener';
import useUser from '@/hooks/useUser';
import { useUserAccess } from '@/hooks/useUserAccess';
import { PlanType } from '@simplywallst/services';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import useReportUrlHandler, {
  CompanySubSection,
  UpdateMethod,
} from '../../hooks/useReportUrlHandler';
import { useCompanyShareLink } from '@/hooks/useCompanyShareToken';
import { useLoadFullReportDetect } from '../../hooks/useLoadFullReportDetect';

interface ContextValue {
  showFullReport: boolean;
  showRegistration: boolean;
  showPlansModal: boolean;
  onIntersectionPathUpdate: (
    data: IntersectionPayload,
    shareToken: string,
    section?: string | undefined
  ) => void;
  goToSection: (section: CompanySubSection, method?: UpdateMethod) => void;
  showFullReportAt: (
    section?: CompanySubSection | undefined,
    method?: UpdateMethod
  ) => void;
  onNavigation: (section: CompanySubSection, method?: UpdateMethod) => void;
  setShowRegistration: (show: boolean) => void;
  setShowPlansModal: (show: boolean) => void;
}

export const ReportSplitHandlerContext = createContext<
  ContextValue | undefined
>(undefined);

export function useReportSplitHandler() {
  const context = useContext(ReportSplitHandlerContext);
  if (context === undefined) {
    throw new Error(
      'useReportUrlHandler must be used within a ReportUrlHandlerProvider'
    );
  }
  return context;
}

interface Props {
  children: React.ReactElement;
}
const ReportSplitHandlerProvider = (props: Props) => {
  const {
    onIntersectionPathUpdate,
    goToSection,
    showFullReportAt,
    showFullReport,
  } = useReportUrlHandler();

  const [showRegistration, setShowRegistration] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const { data: user } = useUser();
  const { data: userAccess } = useUserAccess();
  const summary = useSummaryV2();
  const companyId =
    summary.status === 'success' ? summary.companyId : undefined;
  const limits = useCompanyLimits(companyId);

  const isPremiumUser =
    userAccess.planType === PlanType.Premium &&
    user.billing.status !== 'trialing';
  const hasCredits = !limits.restricted || limits.visited;
  const { isSharedCompany } = useCompanyShareLink();

  const { mutateCompanyView } = useGetMutateCompanyLimit();
  const consumeCreditIfPremiumUser = useCallback(
    () => isPremiumUser && mutateCompanyView(companyId),
    [isPremiumUser, companyId, mutateCompanyView]
  );

  const isLoadFullReportVariant = useLoadFullReportDetect();

  const onNavigation = useCallback(
    (section: CompanySubSection, method = 'push') => {
      if (!hasCredits && !isSharedCompany) {
        setShowPlansModal(true);
        return;
      }

      if (!user.loggedIn && !isSharedCompany && !isLoadFullReportVariant) {
        setShowRegistration(true);
        return;
      }

      if (showFullReport) {
        goToSection(section, method);
      } else {
        showFullReportAt(section, method);
        !isSharedCompany && consumeCreditIfPremiumUser();
      }
    },
    [
      isLoadFullReportVariant,
      user.loggedIn,
      hasCredits,
      showFullReport,
      goToSection,
      showFullReportAt,
      setShowPlansModal,
      setShowRegistration,
      consumeCreditIfPremiumUser,
      isSharedCompany,
    ]
  );

  const value = useMemo(
    () => ({
      showFullReport,
      showRegistration,
      showPlansModal,
      onIntersectionPathUpdate,
      goToSection,
      showFullReportAt,
      onNavigation,
      setShowRegistration,
      setShowPlansModal,
    }),
    [
      showFullReport,
      showRegistration,
      showPlansModal,
      onIntersectionPathUpdate,
      goToSection,
      showFullReportAt,
      onNavigation,
      setShowRegistration,
      setShowPlansModal,
    ]
  );

  return (
    <ReportSplitHandlerContext.Provider value={value}>
      {props.children}
    </ReportSplitHandlerContext.Provider>
  );
};
export default ReportSplitHandlerProvider;
